/**
 * @file useNavigationFacade.hook.ts
 * @path apps/website/src/hooks/useNavigationFacade.hook.ts
 * @description Facade hook to manage state, effects, and data for the Navigation component.
 */

import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useIsMobile } from "@/hooks";
import { MAIN_NAV_ITEMS } from "@/config/paths.config";

export const useNavigationFacade = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);

  const scrollTriggerRef = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();
  const isMobile = useIsMobile();

  /**
   * Handle scroll observation for collapsing the menu.
   * We use a trigger ref placed slightly down the page to toggle 'isCollapsed'.
   */
  useEffect(() => {
    setHasMounted(true);

    const currentRef = scrollTriggerRef.current;
    if (!currentRef) return;

    const scrollObserver = new IntersectionObserver(
      ([entry]) => {
        setIsCollapsed(!entry.isIntersecting);
      },
      { rootMargin: "-70px 0px 0px 0px" },
    );

    scrollObserver.observe(currentRef);

    return () => {
      scrollObserver.disconnect();
    };
  }, []);

  const handleSetIsOpen = useCallback((open: boolean) => {
    setIsOpen(open);
  }, []);

  const actions = useMemo(
    () => ({
      setIsOpen: handleSetIsOpen,
    }),
    [handleSetIsOpen],
  );

  // Derived UI states
  const showFullNav = hasMounted && !isCollapsed && !isMobile;
  const showToggle = hasMounted && (isCollapsed || isMobile);

  return {
    state: {
      isOpen,
      showFullNav,
      showToggle,
      pathname,
      isCollapsed,
    },
    actions,
    refs: {
      scrollTriggerRef,
    },
    data: {
      navLinksArray: MAIN_NAV_ITEMS,
    },
  };
};
